// Ticket #129 phase-02 — self-heal: gỡ pre-push hook cũ (dead code v2, cài
// trước khi installer bị gỡ ở phase-01) cho mọi repo trong src/ (manifest
// .claude/repos.json) CÓ marker Avatar. Hook lạ của member (không marker) giữ
// nguyên — xoá nhầm là phá việc người khác. BACKUP TRƯỚC KHI XOÁ (bất biến,
// không ngoại lệ): copy hook sang backupDir rồi mới rm; copy fail → KHÔNG xoá.
// Không gọi git subprocess — chỉ node:fs, theo precedent patch-all-repos-gitignore.ts.
import { copyFile, lstat, readFile, rm } from "node:fs/promises";
import { join } from "node:path";
import { ensureDir, pathExists } from "./filesystem-helpers.js";
import { readReposManifest } from "./workspace-repos-manifest-store.js";

export const AVATAR_PRE_PUSH_MARKER = "Avatar pre-push hook";

export interface PruneHooksResult {
  pruned: string[]; // repo đã xoá hook (đã backup xong)
  skippedForeign: string[]; // có pre-push nhưng KHÔNG chứa marker Avatar
  skippedNonDirGit: string[]; // `.git` không phải directory (file gitdir / thiếu)
  missing: string[]; // repo trong manifest nhưng thiếu folder
  failed: { name: string; error: string }[];
  backups: { name: string; backupPath: string }[]; // đường dẫn bản sao trước khi xoá
}

function defaultBackupDir(workspaceRoot: string): string {
  return join(workspaceRoot, ".claude", "_backup");
}

// `.git` phải là DIRECTORY để pruner đụng vào. Không tồn tại hoặc là file
// (worktree/submodule "gitdir: ...") đều coi là "không phải dir git" — cùng
// một lý do: không có `.git/hooks/` thật để đọc, không throw.
async function isRealGitDir(gitPath: string): Promise<boolean> {
  if (!(await pathExists(gitPath))) return false;
  const st = await lstat(gitPath);
  return st.isDirectory();
}

export async function pruneStaleAvatarPrePushHooks(
  workspaceRoot: string,
  opts?: { backupDir?: string },
): Promise<PruneHooksResult> {
  const repos = await readReposManifest(workspaceRoot);
  const backupDir = opts?.backupDir ?? defaultBackupDir(workspaceRoot);

  const pruned: string[] = [];
  const skippedForeign: string[] = [];
  const skippedNonDirGit: string[] = [];
  const missing: string[] = [];
  const failed: { name: string; error: string }[] = [];
  const backups: { name: string; backupPath: string }[] = [];

  for (const r of repos) {
    const repoPath = join(workspaceRoot, r.path);
    if (!(await pathExists(repoPath))) {
      missing.push(r.name);
      continue;
    }
    try {
      const gitPath = join(repoPath, ".git");
      if (!(await isRealGitDir(gitPath))) {
        skippedNonDirGit.push(r.name);
        continue;
      }
      const hookPath = join(gitPath, "hooks", "pre-push");
      if (!(await pathExists(hookPath))) {
        continue; // không có hook — bỏ qua im lặng, không đếm vào bucket nào
      }
      const content = await readFile(hookPath, "utf8");
      if (!content.includes(AVATAR_PRE_PUSH_MARKER)) {
        skippedForeign.push(r.name);
        continue;
      }
      // BACKUP TRƯỚC KHI XOÁ — copy fail → không xoá (bất biến, không ngoại lệ).
      await ensureDir(backupDir);
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const backupPath = join(backupDir, `pre-push-${r.name}-${ts}`);
      await copyFile(hookPath, backupPath);
      await rm(hookPath);
      pruned.push(r.name);
      backups.push({ name: r.name, backupPath });
    } catch (err) {
      failed.push({ name: r.name, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return { pruned, skippedForeign, skippedNonDirGit, missing, failed, backups };
}

// ĐỌC-THUẦN: không xoá, không ghi, không tạo backup dir. Dùng cho dry-run/
// preview (phase-03 `uninstall`). Đối xứng precedent
// `patch-all-repos-git-exclude.ts` (`patchAllReposGitExclude` + `findReposMissingAvatarExclude`).
export async function findReposWithAvatarPrePushHook(workspaceRoot: string): Promise<string[]> {
  const repos = await readReposManifest(workspaceRoot);
  const found: string[] = [];
  for (const r of repos) {
    const repoPath = join(workspaceRoot, r.path);
    if (!(await pathExists(repoPath))) continue;
    try {
      const gitPath = join(repoPath, ".git");
      if (!(await isRealGitDir(gitPath))) continue;
      const hookPath = join(gitPath, "hooks", "pre-push");
      if (!(await pathExists(hookPath))) continue;
      const content = await readFile(hookPath, "utf8");
      if (content.includes(AVATAR_PRE_PUSH_MARKER)) found.push(r.name);
    } catch {
      // đọc lỗi 1 repo → bỏ qua repo đó (đọc-thuần, không có `failed`)
    }
  }
  return found;
}
